import { useState, useRef, useEffect } from "react";
import { Link } from "wouter";
import { Search, Shuffle, ShoppingBag } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

interface SearchHeaderProps {
  artworks: any[];
  onArtworkSelect: (artworkId: number) => void;
  onSearch: (query: string) => void;
  totalCount: number;
  onArtworkDisplay?: (artwork: any) => void;
  onRandomArtwork?: () => void;
  currentArtworkId?: number;
  currentArtwork?: { title: string; artist: string } | null;
}

export default function SearchHeader({
  artworks,
  onArtworkSelect,
  onSearch,
  totalCount,
  onArtworkDisplay,
  onRandomArtwork,
  currentArtworkId,
  currentArtwork,
}: SearchHeaderProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Use the passed artworks prop for search results
  const searchResults = artworks;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setShowSuggestions(value.length > 0);
    onSearch(value);
  };

  const handleSuggestionClick = (artwork: any) => {
    setSearchQuery("");
    setShowSuggestions(false);

    // Track search suggestion click
    trackEvent("view_artwork", "navigation", "search_suggestion", artwork.id);

    // Navigate directly to artwork page using its ID if available
    if (artwork.id) {
      window.history.pushState({}, "", `/artwork/${artwork.id}/`);
      // Trigger a popstate event to update the page
      window.dispatchEvent(new PopStateEvent("popstate"));
    } else {
      // Fallback for artworks without ID (legacy)
      const urlParams = new URLSearchParams();
      urlParams.set("artwork", encodeURIComponent(JSON.stringify(artwork)));
      window.history.pushState({}, "", `/?${urlParams.toString()}`);

      if (onArtworkDisplay) {
        onArtworkDisplay(artwork);
      }
    }

    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setShowSuggestions(false);
      inputRef.current?.blur();
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-border">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-base sm:text-xl font-semibold tracking-tight shrink-0">
            <Link href={window.location.pathname + window.location.search} className="hover:text-primary transition-colors">
              Ariadne's Gallery
            </Link>
          </h1>

          <div className="flex-1 min-w-0 max-w-md mx-2 sm:mx-8 relative" ref={searchRef}>
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                placeholder="Search masterpieces..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setShowSuggestions(searchQuery.length > 0)}
                className="w-full px-4 py-2 bg-card border border-input rounded-full focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all search-input"
              />
              <div className="absolute right-3 top-2.5 text-muted-foreground">
                <Search className="w-5 h-5" />
              </div>
            </div>

            {/* Search Suggestions */}
            {showSuggestions && searchResults.length > 0 && (
              <div className="absolute top-full mt-2 w-full bg-card border border-border rounded-lg shadow-lg max-h-96 overflow-auto z-50">
                {searchResults
                  .slice(0, 6)
                  .map((artwork: any, index: number) => (
                    <button
                      key={artwork.title + index}
                      onClick={() => handleSuggestionClick(artwork)}
                      className="w-full px-4 py-3 text-left hover:bg-accent/50 border-b border-border last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <img
                          src={artwork.imageUrl || artwork.url}
                          alt={artwork.title}
                          className="w-10 h-10 object-cover rounded"
                          onError={(e) => {
                            e.currentTarget.src =
                              'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="%23ccc"><rect width="40" height="40" fill="%23f5f5f5"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" font-size="12" fill="%23999">Art</text></svg>';
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {artwork.title}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {artwork.artist}
                          </p>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {artwork.movement}
                        </div>
                      </div>
                    </button>
                  ))}

                {searchResults.length > 6 && (
                  <div className="px-4 py-2 text-xs text-muted-foreground border-t border-border">
                    +{searchResults.length - 6} more results
                  </div>
                )}
              </div>
            )}

            {/* No Results */}
            {showSuggestions &&
              searchQuery.length > 0 &&
              searchResults.length === 0 && (
                <div className="absolute top-full mt-2 w-full bg-card border border-border rounded-lg shadow-lg p-4 z-50">
                  <p className="text-sm text-muted-foreground text-center">
                    No artworks found for "{searchQuery}"
                  </p>
                </div>
              )}
          </div>

          {/* On phones the buttons show icons only so the header fits in 390px. */}
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <Link
              href={currentArtworkId ? `/emotion-search/${currentArtworkId}/` : "/emotion-search/"}
              className="flex items-center gap-2 px-3 py-2 bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full transition-colors text-sm font-medium"
              title="Search by emotion"
              aria-label="Emotion Search"
            >
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline">Emotion Search</span>
            </Link>
            <button
              onClick={() => {
                // Track random button click
                trackEvent("click", "ui_interaction", "random_button");
                onRandomArtwork?.();
              }}
              className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full transition-colors text-sm font-medium"
              title="View random artwork"
              aria-label="Random artwork"
            >
              <Shuffle className="w-4 h-4" />
              <span className="hidden sm:inline">Random</span>
            </button>

            {/* Buy a Print button — commented out pending affiliate setup
            {currentArtwork && (
              <a
                href={`https://www.art.com/gallery/id--a/search.htm?q=${encodeURIComponent(`${currentArtwork.title} ${currentArtwork.artist}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("click", "monetisation", "buy_print_nav")}
                className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full transition-colors text-sm font-medium"
                title="Buy a print of this artwork"
              >
                <ShoppingBag className="w-4 h-4" />
                Buy a Print
              </a>
            )}
            */}

            <div className="hidden sm:block text-sm text-muted-foreground">
              {totalCount.toLocaleString()} works
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
